import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { feedbackApi } from "../services/feedbackApi";
import { getDiagnostics } from "../services/diagnostics";

export default function BugReportButton() {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" as string });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  if (!isAuthenticated) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);

    try {
      await feedbackApi.submit({
        type: "bug",
        title: form.title,
        description: form.description,
        priority: form.priority,
        organizationId: user?.organizationId || "",
        submittedBy: `${user?.firstName} ${user?.lastName}`,
        submittedByEmail: user?.email || "",
        diagnostics: getDiagnostics(),
      } as any);
      setStatus({ type: "success", message: "Bug reported — thank you!" });
      setForm({ title: "", description: "", priority: "medium" });
      setTimeout(() => { setOpen(false); setStatus(null); }, 2000);
    } catch (err) {
      setStatus({ type: "error", message: err instanceof Error ? err.message : "Failed to submit" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Report a bug"
        style={{
          position: "fixed",
          bottom: "1.5rem",
          right: "1.5rem",
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: "#dc2626",
          color: "white",
          border: "none",
          fontSize: "1.3rem",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          cursor: "pointer",
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        title="Report a Bug"
      >
        🐛
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "2rem",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              padding: "2rem",
              width: "100%",
              maxWidth: "480px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: "0.25rem", fontSize: "1.15rem" }}>🐛 Report a Bug</h3>
            <p style={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: "1.25rem" }}>
              Browser info, console errors, and current page are captured automatically.
            </p>

            {status && (
              <div style={{ marginBottom: "1rem", fontSize: "0.85rem", color: status.type === "success" ? "#16a34a" : "#dc2626" }}>
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gap: "1rem" }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.825rem", fontWeight: 500, marginBottom: "0.3rem" }}>Title</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="What went wrong?"
                    required
                    style={{ width: "100%", padding: "0.6rem 0.8rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.9rem" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.825rem", fontWeight: 500, marginBottom: "0.3rem" }}>Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Steps to reproduce..."
                    required
                    rows={4}
                    style={{ width: "100%", padding: "0.6rem 0.8rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.9rem", fontFamily: "inherit", resize: "vertical" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.825rem", fontWeight: 500, marginBottom: "0.3rem" }}>Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    style={{ width: "100%", padding: "0.6rem 0.8rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.9rem" }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{ padding: "0.6rem 1.25rem", background: "white", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.9rem", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: "0.6rem 1.25rem", background: "#2563eb", color: "white", border: "none", borderRadius: "6px", fontSize: "0.9rem", fontWeight: 500, cursor: "pointer" }}
                >
                  {submitting ? "Submitting..." : "Submit Bug"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
